<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { picoSearch } from '@chatwoot/pico-search';

import { BaseTable } from 'dashboard/components-next/table';
import DeleteTaskDialog from './delete/DeleteTaskDialog.vue';
import TaskListTableRow from './TaskListTableRow.vue';

const props = defineProps({
  searchQuery: {
    type: String,
    default: '',
  },
});

const store = useStore();
const getters = useStoreGetters();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const deleteTaskDialogRef = ref(null);

const records = computed(() => getters['wgptTasks/getTasks'].value);

onMounted(() => {
  store.dispatch('wgptTasks/get');
});

const filteredRecords = computed(() => {
  const query = props.searchQuery.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['name', 'description', 'type']);
});

const tableHeaders = computed(() => [
  t('WGPT_TASKS.TASKS.COLUMN.NAME'),
  t('WGPT_TASKS.TASKS.COLUMN.TYPE'),
  t('WGPT_TASKS.TASKS.COLUMN.ACTIVE'),
  t('WGPT_TASKS.TASKS.COLUMN.UPDATED'),
]);

const handleEditTask = task => {
  router.push({
    name: 'wgpt_tasks_editor',
    params: {
      ...route.params,
      taskId: task.id,
    },
  });
};

const handleDeleteTask = task => {
  deleteTaskDialogRef.value.open(task);
};

const handleToggleTask = task => {
  store.dispatch('wgptTasks/update', {
    id: task.id,
    wgpt_automation_attributes: {
      active: !task.active,
    },
  });
};
</script>

<template>
  <div class="flex flex-col h-full">
    <BaseTable
      :headers="tableHeaders"
      :items="filteredRecords"
      :no-data-message="
        searchQuery ? $t('WGPT_TASKS.NO_RESULTS') : $t('WGPT_TASKS.LIST.404')
      "
    >
      <template #row="{ items }">
        <TaskListTableRow
          v-for="task in items"
          :key="task.id"
          :task="task"
          @edit="handleEditTask"
          @delete="handleDeleteTask"
          @toggle="handleToggleTask"
        />
      </template>
    </BaseTable>
    <DeleteTaskDialog ref="deleteTaskDialogRef" />
  </div>
</template>
